import { LOCATION_CHANGED, LocationChangedAction } from 'redux-little-router';

import { Action } from '../actions';
import { getColumnsSettings } from '../declarations/common';
import { RoutesPath } from '../routes/index';
import { getKeywordsEditorInitialState } from '../service/keywordsEditor';
import { LogInitialState } from '../service/LogHelpers/LogHelpers';
import { getInitialMinusPhraseLibraryEditorState } from '../service/MinusPhraseService/MinusPhraseService';
import { recommendationsInitialState } from '../service/RecommendationsHelpers/RecommendationsHelpers';
import { ReducerCustom } from '../service/Redux/Redux';
import { getStrategyEditorsInitialState } from '../service/StrategyEditorHelpers/StrategyEditorHelpers';
import { UrlHelpers } from '../service/UrlHelpers';
import { EditBannersState } from '../typings/bannersEditState';
import { EditCampaignsState } from '../typings/campaignsEdit';
import { ChatListEditorState } from '../typings/chatList';
import { PageStatus } from '../typings/common';
import { defaultRowsCount } from '../typings/grid';
import { EditGroupsState } from '../typings/groupsEditState';
import { ImportExportWizardState } from '../typings/importExportWizard';
import { EditInternalBannersState } from '../typings/internalBannersEditState';
import { InternalProductPlacesState } from '../typings/internalProductPlace';
import { KeywordsEditorsState } from '../typings/keywordsEditorData';
import { MarketersState, MarketerState } from '../typings/marketers';
import { MessagesState } from '../typings/messagesState';
import { MetrikaCounterEditorState } from '../typings/metrikaCounterEditor';
import { PricePackagesEditState, PricePackagesState } from '../typings/pricePackages';
import { ProductsState } from '../typings/products';
import { RetargetingConditionEditorState } from '../typings/retargeting';
import {
    CommonState,
    CryptaAudioGenresEditorsState,
    CryptaBehavioralEditorsState,
    CryptaGroupFamilyEditorsState,
    CryptaSocialDemoEditorsState,
    GridState,
    HelpState,
    ImageUploaderState,
    LogState,
    MinusPhraseLibraryEditorState,
    RecommendationsGridState,
    RecommendationsState,
    State
} from '../typings/state';
import { StrategyEditorState } from '../typings/strategyEditor';
import { getVideoCreatorEditPageInitialState } from '../video-creator/VideoCreatorEditPage/reducer/reducer';
import { VideoCreatorEditState } from '../video-creator/VideoCreatorEditPage/typings';
import { imageUploaderInitialState } from '../video-creator/VideoCreatorImageUploader/reducer';

import { getEditBannerPageData } from './bannersEdit';
import { getEditInternalBannerPageData } from './bannersEdit/internal';
import { getEditCampaignsPageData } from './campaignsEdit';
import { chatListInitialState } from './chatListEditor/chatListEditor';
import { resetSidebar } from './common/helpers';
import { resetGridStateReducer } from './grid/helpers';
import { getEditGroupPageData } from './groupsEdit';
import { helpInitialState } from './help';
import { getMarketerInitialState } from './marketer/internal';
import { getMarketersInitialState } from './marketers/internal';
import { metrikaCounterEditorInitialState } from './metrikaCounterEditor';
import { getInternalProductPlacesInitialState } from './places/internal';
import { pricePackagesInitialState } from './pricePackages';
import { getPricePackagesEditPageData } from './pricePackagesEdit';
import { getProductsInitialState } from './products/internal';
import { defaultRetargetingEditorState } from './retargetingConditionEditor';
import { cryptaAudioGenresEditorsInitialState } from './targetings/cryptaAudioGenres';
import { cryptaBehavioralEditorsInitialState } from './targetings/cryptaBehavioral';
import { cryptaGroupFamilyEditorsInitialState } from './targetings/cryptaGroupFamily';
import { cryptaSocialDemoEditorsInitialState } from './targetings/cryptaSocialDemo';

const gridRouteFlow = (state: GridState, action: LocationChangedAction, fullState: State): GridState => {
    const { route: newRoute } = action.payload;
    const previousRouter = fullState.router.previous;
    if (
        newRoute === RoutesPath.grid ||
        newRoute === RoutesPath.shortGrid ||
        newRoute === RoutesPath.bannersEdit ||
        newRoute === RoutesPath.campaignsEdit ||
        newRoute === RoutesPath.groupsEdit ||
        newRoute === RoutesPath.internalBannersEdit
    ) {
        if (previousRouter) {
            if (
                previousRouter.route === RoutesPath.bannersEdit ||
                previousRouter.route === RoutesPath.campaignsEdit ||
                previousRouter.route === RoutesPath.groupsEdit ||
                previousRouter.route === RoutesPath.grid ||
                previousRouter.route === RoutesPath.internalBannersEdit
            ) {
                return resetGridStateReducer(state, fullState, state.selectionInfo);
            }
        }
    }

    return resetGridStateReducer(state, fullState);
};

const commonRouteFlow = (state: CommonState): CommonState => {
    return {
        ...resetSidebar(state),
        screenError: {
            isVisible: false
        },
        outdoorMapData: undefined,
        indoorMapData: undefined
    };
};

const recommendationsRouteFlow = (state: RecommendationsState, action: LocationChangedAction): RecommendationsState => {
    if (action.payload.route !== RoutesPath.recommendationsPage) {
        return state;
    }

    return recommendationsInitialState;
};

const recommendationsGridRouteFlow = (
    state: RecommendationsGridState,
    action: LocationChangedAction
): RecommendationsGridState => {
    if (action.payload.route !== RoutesPath.recommendationsItems) {
        return state;
    }

    return {
        ...state,
        summaryRecommendationData: undefined,
        gridData: {
            ...state.gridData,
            allItemsIds: [],
            data: [],
            total: defaultRowsCount
        }
    };
};

const logRouteFlow = (state: LogState, action: LocationChangedAction, fullState: State): LogState => {
    const previousRouter = fullState.router.previous;
    const { route, search } = action.payload;
    const filterNotChanged = previousRouter ? previousRouter.search === search : true;
    if (route !== RoutesPath.log || filterNotChanged) {
        return state;
    }

    return {
        ...LogInitialState,
        columnsSettings: getColumnsSettings()
    };
};

const helpRouteFlow = (state: HelpState, action: LocationChangedAction): HelpState => {
    return helpInitialState;
};

const editGroupsFlow = (state: EditGroupsState, action: LocationChangedAction, fullState: State): EditGroupsState => {
    if (action.payload.route !== RoutesPath.groupsEdit) {
        return state;
    }

    // redux-little-router не умеет парсить query, если он был передан строкой, без query параметров
    const queryParams = UrlHelpers.parseQueryString(document.location.search);

    return {
        ...state,
        pageStatus: PageStatus.LOADING,
        ...getEditGroupPageData(queryParams)
    };
};

const editCampaignsFlow = (
    state: EditCampaignsState,
    action: LocationChangedAction,
    fullState: State
): EditCampaignsState => {
    if (action.payload.route !== RoutesPath.campaignsEdit) {
        return state;
    }

    // redux-little-router не умеет парсить query, если он был передан строкой, без query параметров
    const queryParams = UrlHelpers.parseQueryString(document.location.search);

    return {
        ...state,
        ...getEditCampaignsPageData(queryParams)
    };
};

const editBannersFlow = (
    state: EditBannersState,
    action: LocationChangedAction,
    fullState: State
): EditBannersState => {
    if (action.payload.route !== RoutesPath.bannersEdit) {
        return state;
    }

    // redux-little-router не умеет парсить query, если он был передан строкой, без query параметров
    const queryParams = UrlHelpers.parseQueryString(document.location.search);

    return {
        ...state,
        ...getEditBannerPageData(queryParams)
    };
};

const editInternalBannersFlow = (
    state: EditInternalBannersState,
    action: LocationChangedAction,
    fullState: State
): EditInternalBannersState => {
    if (action.payload.route !== RoutesPath.internalBannersEdit) {
        return state;
    }

    // redux-little-router не умеет парсить query, если он был передан строкой, без query параметров
    const queryParams = UrlHelpers.parseQueryString(document.location.search);

    return {
        ...state,
        ...getEditInternalBannerPageData(queryParams)
    };
};

const retargetingConditionEditorFlow = (state: RetargetingConditionEditorState): RetargetingConditionEditorState => {
    return defaultRetargetingEditorState;
};

const chatListEditorStateFlow = (state: ChatListEditorState): ChatListEditorState => {
    return chatListInitialState;
};

const metrikaCounterEditorFlow = (state: MetrikaCounterEditorState): MetrikaCounterEditorState => {
    return metrikaCounterEditorInitialState;
};

const minusPhraseLibraryEditorFlow = (state: MinusPhraseLibraryEditorState): MinusPhraseLibraryEditorState => {
    return getInitialMinusPhraseLibraryEditorState();
};

const keywordsEditorFlow = (state: KeywordsEditorsState): KeywordsEditorsState => {
    return getKeywordsEditorInitialState();
};

const strategyEditorFlow = (state: StrategyEditorState): StrategyEditorState => {
    return getStrategyEditorsInitialState();
};

const cryptaSocialDemoEditorStateFlow = (state: CryptaSocialDemoEditorsState): CryptaSocialDemoEditorsState => {
    return cryptaSocialDemoEditorsInitialState;
};

const cryptaGroupFamilyEditorStateFlow = (state: CryptaGroupFamilyEditorsState): CryptaGroupFamilyEditorsState => {
    return cryptaGroupFamilyEditorsInitialState;
};

const cryptaBehavioralEditorStateFlow = (state: CryptaBehavioralEditorsState): CryptaBehavioralEditorsState => {
    return cryptaBehavioralEditorsInitialState;
};

const cryptaAudioGenresEditorStateFlow = (state: CryptaAudioGenresEditorsState): CryptaBehavioralEditorsState => {
    return cryptaAudioGenresEditorsInitialState;
};

const internalProductsFlow = (state: ProductsState, action: LocationChangedAction, fullState: State): ProductsState => {
    if (action.payload.route !== RoutesPath.products) {
        return state;
    }

    const { route: newRoute } = action.payload;
    const { route: oldRoute } = fullState.router;

    if (newRoute === oldRoute) {
        return state;
    }

    return getProductsInitialState();
};

const internalProductPlacesFlow = (
    state: InternalProductPlacesState,
    action: LocationChangedAction,
    fullState: State
): InternalProductPlacesState => {
    if (action.payload.route !== RoutesPath.products) {
        return state;
    }

    const { route: newRoute } = action.payload;
    const { route: oldRoute } = fullState.router;

    if (newRoute === oldRoute) {
        return state;
    }

    return getInternalProductPlacesInitialState();
};

const importExportWizardFlow = (
    state: ImportExportWizardState,
    action: LocationChangedAction
): ImportExportWizardState => {
    if (action.payload.route === RoutesPath.toolsExcel) {
        return {
            ...state,
            isVisible: true
        };
    }

    return state;
};

const marketersListFlow = (state: MarketersState, action: LocationChangedAction, fullState: State): MarketersState => {
    const { route: newRoute } = action.payload;
    const { route: oldRoute } = fullState.router;

    if (newRoute === oldRoute) {
        return state;
    }

    return getMarketersInitialState();
};

const marketerCardFlow = (state: MarketerState, action: LocationChangedAction, fullState: State): MarketerState => {
    const { route: newRoute } = action.payload;
    const { route: oldRoute } = fullState.router;

    if (newRoute === oldRoute) {
        return state;
    }

    return getMarketerInitialState();
};

const routeFlowWrapper = <K extends keyof State, T extends State[K]>(
    reducer: ReducerCustom<T>,
    routeFlowReducer: ReducerCustom<T>
): ReducerCustom<T> => {
    return (state: T, action: Action, fullState: State): T => {
        if (action.type === LOCATION_CHANGED) {
            return routeFlowReducer(state, action, fullState);
        }
        return reducer(state, action, fullState);
    };
};

const pricePackagesFlow = (state: PricePackagesState): PricePackagesState => {
    return pricePackagesInitialState;
};

const editPricePackagesFlow = (
    state: PricePackagesEditState,
    action: LocationChangedAction,
    fullState: State
): PricePackagesEditState => {
    if (action.payload.route !== RoutesPath.pricePackagesEdit) {
        return state;
    }

    // redux-little-router не умеет парсить query, если он был передан строкой, без query параметров
    const queryParams = UrlHelpers.parseQueryString(document.location.search);

    return {
        ...state,
        pageStatus: PageStatus.LOADING,
        ...getPricePackagesEditPageData(queryParams)
    };
};

const messagesFlow = (state: MessagesState, action: LocationChangedAction, fullState: State): MessagesState => {
    if (state.items.length > 0) {
        return {
            ...state,
            items: []
        };
    }

    return state;
};

const videoCreatorEditFlow = (state: VideoCreatorEditState): VideoCreatorEditState => {
    return getVideoCreatorEditPageInitialState();
};

const imageUploaderEditorFlow = (state: ImageUploaderState): ImageUploaderState => {
    return imageUploaderInitialState;
};

export const withRouteFlow = <K extends keyof State>(
    reducers: Record<K, ReducerCustom<State[K]>>
): Record<K, ReducerCustom<State[K]>> => {
    return Object.keys(reducers).reduce(
        (acc, key: K) => {
            const reducer = reducers[key];
            let routeFlow: ReducerCustom<State[K]> | undefined = undefined;
            switch (key) {
                case 'recommendations':
                    routeFlow = recommendationsRouteFlow;
                    break;
                case 'recommendationsGrid':
                    routeFlow = recommendationsGridRouteFlow;
                    break;
                case 'commonData':
                    routeFlow = commonRouteFlow;
                    break;
                case 'grid':
                    routeFlow = gridRouteFlow;
                    break;
                case 'log':
                    routeFlow = logRouteFlow;
                    break;
                case 'help':
                    routeFlow = helpRouteFlow;
                    break;
                case 'editCampaigns':
                    routeFlow = editCampaignsFlow;
                    break;
                case 'editGroups':
                    routeFlow = editGroupsFlow;
                    break;
                case 'editBanners':
                    routeFlow = editBannersFlow;
                    break;
                case 'editInternalBanners':
                    routeFlow = editInternalBannersFlow;
                    break;
                case 'products':
                    routeFlow = internalProductsFlow;
                    break;
                case 'internalProductPlaces':
                    routeFlow = internalProductPlacesFlow;
                    break;
                case 'importExportWizard':
                    routeFlow = importExportWizardFlow;
                    break;
                case 'cryptaSocialDemoEditor':
                    routeFlow = cryptaSocialDemoEditorStateFlow;
                    break;
                case 'cryptaGroupFamilyEditor':
                    routeFlow = cryptaGroupFamilyEditorStateFlow;
                    break;
                case 'cryptaBehavioralEditor':
                    routeFlow = cryptaBehavioralEditorStateFlow;
                    break;
                case 'cryptaAudioGenresEditor':
                    routeFlow = cryptaAudioGenresEditorStateFlow;
                    break;
                case 'marketersList':
                    routeFlow = marketersListFlow;
                    break;
                case 'marketerCard':
                    routeFlow = marketerCardFlow;
                    break;
                case 'audienceTargetingsEditor':
                case 'interestsTargetingEditor':
                case 'itemsListTextareaEditor':
                case 'notificationEditor':
                case 'rateCorrectionsEditor':
                case 'retargetingEditor':
                case 'regionsEditor':
                case 'datesIntervalEditor':
                case 'experimentsEditor':
                case 'editPageItemNameEditor':
                case 'metrikaKeyGoalsEditor':
                case 'timeTargetingEditor':
                case 'relevantPhrasesEditor':
                case 'outdoorMapEditor':
                case 'indoorMapEditor':
                case 'retargetingAudienceEditor':
                case 'visitingCardEditor':
                case 'piTagsEditor':
                    routeFlow = (): {} => ({});
                    break;
                case 'minusPhraseLibraryEditor':
                    routeFlow = minusPhraseLibraryEditorFlow;
                    break;
                case 'keywordsEditor':
                    routeFlow = keywordsEditorFlow;
                    break;
                case 'retargetingConditionEditor':
                    routeFlow = retargetingConditionEditorFlow;
                    break;
                case 'chatListEditor':
                    routeFlow = chatListEditorStateFlow;
                    break;
                case 'metrikaCounterEditor':
                    routeFlow = metrikaCounterEditorFlow;
                    break;
                case 'strategyEditor':
                    routeFlow = strategyEditorFlow;
                    break;
                case 'pricePackages':
                    routeFlow = pricePackagesFlow;
                    break;
                case 'editPricePackages':
                    routeFlow = editPricePackagesFlow;
                    break;
                case 'messages':
                    routeFlow = messagesFlow;
                    break;
                case 'videoCreatorEdit':
                    routeFlow = videoCreatorEditFlow;
                    break;
                case 'imageUploaderEditor':
                    routeFlow = imageUploaderEditorFlow;
                    break;
                default:
                    break;
            }
            return {
                ...acc,
                [key]: routeFlow ? routeFlowWrapper(reducer, routeFlow) : reducer
            };
        },
        {} as Record<K, ReducerCustom<State[K]>>
    );
};
